import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api, formatZAR, formatApiError } from "@/lib/api";
import { FileText, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const MATTER_LABEL = {
  conveyancing: "Property transfer",
  antenuptial:  "Antenuptial contract",
  trust:        "Trust formation",
};

export default function Proforma() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pf, setPf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("managed");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/proforma/${id}`);
        setPf(data);
        setMode(data.pricing_mode || "managed");
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const pricing = pf?.pricing;

  if (loading) {
    return (
      <div className="min-h-screen bg-background grain">
        <SiteHeader />
        <div className="p-20 text-center text-zinc-500" data-testid="proforma-loading">Loading pro-forma…</div>
      </div>
    );
  }

  if (error || !pf) {
    return (
      <div className="min-h-screen bg-background grain">
        <SiteHeader />
        <div className="p-20 text-center text-red-700" data-testid="proforma-error">{error || "Pro-forma not found"}</div>
      </div>
    );
  }

  const accepted = pf.status === "accepted";

  const acceptProforma = async () => {
    if (accepting) return;
    setAccepting(true);
    try {
      const { data } = await api.post(`/proforma/${id}/accept`, { pricing_mode: mode });
      toast.success(data.already_accepted ? "Already accepted — opening your portal." : "Pro-forma accepted — opening your matter.");
      navigate(`/client/${data.portal_token}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain" data-testid="proforma-page">
      <SiteHeader />
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-5xl px-6 pt-16 pb-10">
          <div className="flex items-center gap-2 text-overline text-zinc-500">
            <FileText className="h-4 w-4" /> Pro-forma
          </div>
          <h1 className="mt-3 font-serif text-5xl lg:text-6xl font-medium tracking-tighter leading-none">
            {MATTER_LABEL[pf.matter_type] || pf.matter_type}
          </h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">
            Issued to <b>{pf.name}</b> · <span className="font-mono text-xs">{pf.email}</span>
          </p>
          <p className="text-xs text-zinc-500 mt-2">
            Valid until {pf.expires_at ? new Date(pf.expires_at).toLocaleDateString() : "—"} · Pro-forma #{pf.id.slice(0, 8)}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-12 space-y-8">
        {/* Mode toggle */}
        <div className="grid md:grid-cols-2 gap-4" data-testid="proforma-modes">
          {["managed", "assisted"].map((k) => {
            const p = pricing[k];
            const isActive = mode === k;
            return (
              <button
                key={k}
                onClick={() => setMode(k)}
                className={`text-left p-6 border transition-colors ${
                  isActive ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white hover:border-zinc-400"
                }`}
                data-testid={`proforma-mode-${k}`}
              >
                <div className={`text-overline ${isActive ? "text-zinc-400" : "text-zinc-500"}`}>{k}</div>
                <div className="font-serif text-2xl mt-2">{p.label}</div>
                <div className={`mt-3 text-sm ${isActive ? "text-zinc-300" : "text-zinc-600"}`}>{p.description}</div>
                <div className="mt-6 font-serif text-3xl tracking-tight">
                  {formatZAR(p.min)} <span className="text-zinc-400 text-base">— {formatZAR(p.max)}</span>
                </div>
                <div className={`mt-1 text-xs ${isActive ? "text-zinc-400" : "text-zinc-500"} font-mono`}>
                  Mid estimate: {formatZAR(p.mid)} · ZAR
                </div>
              </button>
            );
          })}
        </div>

        {/* Breakdown */}
        <div className="bg-white border border-zinc-200 p-8" data-testid="proforma-breakdown">
          <div className="text-overline text-zinc-500">Breakdown</div>
          <h2 className="font-serif text-3xl mt-2">How {mode} pricing is built</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-x-12 gap-y-2 text-sm">
            <BreakdownRow label="Base legal fee" value={pricing.breakdown.base_legal_fee} />
            <BreakdownRow label="Admin component" value={pricing.breakdown.admin_component} />
            {pricing.breakdown.surcharges.map((s, i) => (
              <BreakdownRow key={i} label={`Surcharge: ${s.label}`} value={s.amount} />
            ))}
            <BreakdownRow label="Legal subtotal" value={pricing.breakdown.legal_subtotal} bold />
            <BreakdownRow label="VAT on legal (15%)" value={pricing.breakdown.vat_on_legal} />
            {pricing.breakdown.disbursements > 0 && (
              <>
                <BreakdownRow label="SARS transfer duty" value={pricing.breakdown.sars_transfer_duty} />
                <BreakdownRow label="Deeds office" value={pricing.breakdown.deeds_office_fee} />
                <BreakdownRow label="Disbursements total" value={pricing.breakdown.disbursements} bold />
              </>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
            Variability of ±{pricing.spread_pct}% applies to the legal/admin portion only — SARS transfer duty
            and Deeds Office fees are exact statutory lookups. Source tables: {Object.values(pricing.regulatory_source_versions || {}).join(" · ")}.
          </p>
        </div>

        {/* Acceptance — creates the Client+Matter and redirects to the portal. */}
        <div className="bg-zinc-900 text-white p-10 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-overline text-zinc-400">
              <ShieldCheck className="h-4 w-4" /> Open your matter
            </div>
            <h3 className="font-serif text-3xl mt-2">
              {accepted ? "This pro-forma has been accepted" : "Accept this pro-forma to open your matter"}
            </h3>
            <p className="mt-2 text-zinc-400 text-sm max-w-xl">
              {accepted
                ? "Pricing is locked and your matter is open. Use the link below to jump into your client portal."
                : "Pick the service tier above, then click Accept. We'll open your client file, build your action plan, and take you straight to your portal."}
            </p>
            {!accepted && (
              <div className="mt-5 inline-flex items-center gap-3 flex-wrap">
                <span className="text-xs text-zinc-400">Selected tier:</span>
                {["managed", "assisted"].map((k) => (
                  <button
                    key={k}
                    onClick={() => setMode(k)}
                    className={`text-xs uppercase tracking-widest px-3 py-1.5 border ${
                      mode === k
                        ? "bg-white text-zinc-900 border-white"
                        : "border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                    }`}
                    data-testid={`proforma-mode-${k}`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="text-xs font-mono text-zinc-400">Status: {pf.status}</div>
            <button
              onClick={acceptProforma}
              disabled={accepting}
              className="bg-[#0055FF] hover:bg-[#003ECC] text-white px-5 py-3 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="proforma-accept-btn"
            >
              {accepting ? "Opening your matter…" : accepted ? "Go to my portal" : `Accept (${mode})`}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <p className="text-xs text-zinc-500 leading-relaxed">
          Pricing config version {pricing.config_version_used}. This pro-forma is generated by EH Inc&apos;s deterministic pricing engine. Not legal advice.
        </p>
      </section>

      <SiteFooter />
    </div>
  );
}

function BreakdownRow({ label, value, bold }) {
  return (
    <div className={`flex items-baseline justify-between border-b border-zinc-100 py-2 ${bold ? "font-medium" : ""}`}>
      <span className="text-zinc-600">{label}</span>
      <span className="font-mono">{formatZAR(value)}</span>
    </div>
  );
}
